import { type ReactNode, useEffect, type FC } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Flex, Skeleton, Text } from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { useTheme } from '@/contexts/ThemeContext';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { IfRender, MapRender } from '@/utils/jsx';
export const InvestBalanceAndProfileCard: FC<InvestBalanceAndProfileCardProps> = ({
  isLoading,
  balance,
  footerInfo
}) => {
  const {
    t
  } = useTranslation('common');
  const {
    isDarkMode
  } = useTheme();
  useEffect(() => {
    window.dispatchEvent(new Event('recheck-monetary-visibility'));
  }, [balance]);
  return <Flex data-testid="invest-balance-and-profile-card" display="inline-flex" flexDir="column" gap="2rem" p="2rem" w="27rem" h="26.25rem" bgColor="neutral.50" border="1px solid" borderColor="transparency.light" borderRadius="lg" data-sentry-element="Flex" data-sentry-component="InvestBalanceAndProfileCard" data-sentry-source-file="index.tsx">
      <Flex alignItems="center" gap="12" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <IfRender condition={isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <Skeleton w="15rem" h="2.1875rem" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </IfRender>
        <IfRender condition={!isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <Text fontSize="sm" fontWeight="light" lineHeight="4" color="content.primary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            {t('attributes.total')}
          </Text>
          <Text className="hide-monetary-left" minW="9.375rem" fontSize="2rem" fontWeight="normal" lineHeight="9" color="content.primary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            {balance?.total && balance?.total > 0 ? formatNumberToPTBRCurrency(balance?.total) : 'R$ 0'}
          </Text>
        </IfRender>
      </Flex>
      <Flex flexDir="column" gap="0.75rem" w="full" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <IfRender condition={isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <MapRender items={Array.from({
          length: 3
        })} render={() => <Flex w="full" flexDir="row" gap="3" alignItems="center" justifyContent="space-between" my="0.3rem">
                <Skeleton w="8.125rem" h="0.9375rem" />
                <Skeleton w="6.25rem" h="0.9375rem" />
              </Flex>} data-sentry-element="MapRender" data-sentry-source-file="index.tsx" />
        </IfRender>
        <IfRender condition={!isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <MapRender items={balance?.accounts} render={({
          label,
          value
        }) => <Flex key={crypto.randomUUID()} justifyContent="space-between" alignItems="center" w="full">
                <Text fontSize="md" fontWeight="normal" lineHeight="5" color="content.primary">
                  {label}
                </Text>
                <Flex className="hide-monetary-right" position="relative" justifyContent="flex-end" alignItems="center" minW="8.5rem">
                  <Text fontSize="md" lineHeight="5" fontWeight="normal" color={getValuesColor(value, isDarkMode)}>
                    {!value ? t('attributes.noneWithPrep') : formatNumberToPTBRCurrency(value)}
                  </Text>
                </Flex>
              </Flex>} data-sentry-element="MapRender" data-sentry-source-file="index.tsx" />
        </IfRender>
      </Flex>
      <Divider borderTop="1px solid" borderColor="border.secondary" opacity={1} my={12} data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      <Flex justifyContent="space-between" alignItems="center" w="full" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <IfRender condition={isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <Flex w="full" flexDir="row" gap="3" alignItems="center" justifyContent="space-between" my="0.3rem" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            <Skeleton w="10rem" h="1.375rem" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            <Skeleton w="5rem" h="5rem" rounded="full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          </Flex>
        </IfRender>

        <IfRender condition={!isLoading} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <Text fontSize="md" fontWeight="normal" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            {footerInfo?.title}
          </Text>
          {footerInfo?.chart || <></>}
        </IfRender>
      </Flex>
    </Flex>;
};
const getValuesColor = (value: number | null, isDarkMode: boolean) => {
  if (!value) {
    return 'content.brand';
  }
  if (isDarkMode) {
    return 'neutral.50';
  }
  return 'content.primary';
};
type InvestBalanceAndProfileCardProps = {
  isLoading: boolean;
  balance: InvestCardBalanceProps;
  footerInfo: InvestCardFooterProps;
};
type InvestCardBalanceProps = {
  total: number;
  accounts: InvestCardAccountProps[];
};
type InvestCardAccountProps = {
  id: string;
  label: string;
  value: number | null;
};
type InvestCardFooterProps = {
  title: string;
  chart: ReactNode;
};